/**
 * Revisión guiada de captura OCR — 4 pasos:
 * 1. Proveedor  → validar/crear
 * 2. Productos  → matchear línea por línea o crear
 * 3. Stock      → propuesta de actualización
 * 4. Confirmar  → aplicar cambios
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { RenataFeatureGate } from "@/components/renata/RenataFeatureGate";
import { useState, useEffect } from "react";
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import {
  Building2, Package, BarChart3, CheckCircle2,
  AlertCircle, ChevronRight, Loader2, Plus, Search,
  ArrowLeft, Check, X as XIcon,
} from "lucide-react";
import { DOC_CLASS_ES, CONFIDENCE_ES, OCR_ENGINE_ES, INBOX_STATUS_ES, labelStatus } from "@/lib/renata/experience/status-labels";

export const Route = createFileRoute("/app/renata/capture/$captureId")({
  component: CaptureReviewPage,
  head: () => ({ meta: [{ title: "Revisión de documento — Renata" }] }),
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface CaptureDoc {
  id: string;
  doc_class: string;
  overall_confidence: string;
  filename: string;
  ocr_json: {
    text?: string;
    fields?: Array<{ key: string; value: string; confidence: string }>;
    engine?: string;
    rawNotes?: string[];
  };
  structured_json: {
    data?: {
      supplier_name?: string;
      supplier_cuit?: string;
      supplier_email?: string;
      document_number?: string;
      issue_date?: string;
      total?: number;
      items?: Array<{
        description: string;
        quantity: number;
        unit?: string;
        unit_price?: number;
        subtotal?: number;
        sku?: string | null;
      }>;
    };
  };
  entities_json: Array<{
    kind: string;
    label: string;
    value: string;
    confidence: string;
    matchedId?: string | null;
  }>;
  matches_json: Array<{
    entityKind: string;
    candidateId: string;
    candidateLabel: string;
    score: number;
    confidence: string;
  }>;
  status: string;
  needs_review: boolean;
}

interface LlmStructured {
  tipo?: string;
  proveedor?: {
    nombre?: string | null;
    cuit?: string | null;
    email?: string | null;
    telefono?: string | null;
    direccion?: string | null;
  };
  numero_documento?: string | null;
  fecha?: string | null;
  items?: Array<{
    descripcion: string;
    cantidad: number;
    unidad?: string;
    precio_unitario?: number;
    subtotal?: number;
    codigo?: string | null;
  }>;
  total?: number | null;
  iva?: number | null;
}

interface Supplier {
  id: string;
  name?: string;
  nombre?: string;
  cuit?: string;
  email?: string;
  activo?: boolean;
}

interface Product {
  id: string;
  name?: string;
  nombre?: string;
  sku?: string;
  stockActual?: number | null;
  stock?: number | null;
  activo?: boolean;
}

// ── Step definitions ──────────────────────────────────────────────────────────

const STEPS = [
  { id: "supplier",  label: "Proveedor",   icon: Building2  },
  { id: "products",  label: "Productos",   icon: Package    },
  { id: "stock",     label: "Stock",       icon: BarChart3  },
  { id: "confirm",   label: "Confirmar",   icon: CheckCircle2 },
] as const;

type StepId = typeof STEPS[number]["id"];

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseLlmStructured(ocr: CaptureDoc["ocr_json"]): LlmStructured | null {
  const note = (ocr.rawNotes ?? []).find((n) => n.startsWith("__llm_structured__"));
  if (!note) return null;
  try {
    return JSON.parse(note.replace("__llm_structured__", "")) as LlmStructured;
  } catch {
    return null;
  }
}

function extractSupplierHint(doc: CaptureDoc): { nombre?: string; cuit?: string; email?: string } {
  const llm = parseLlmStructured(doc.ocr_json);
  if (llm?.proveedor?.nombre) {
    return {
      nombre: llm.proveedor.nombre ?? undefined,
      cuit: llm.proveedor.cuit ?? undefined,
      email: llm.proveedor.email ?? undefined,
    };
  }
  // Fallback a structured_json
  const sd = doc.structured_json?.data;
  if (sd?.supplier_name) {
    return {
      nombre: sd.supplier_name,
      cuit: sd.supplier_cuit,
      email: sd.supplier_email,
    };
  }
  // Fallback a entities
  const entity = doc.entities_json?.find((e) => e.kind === "proveedor");
  if (entity) return { nombre: entity.value };
  return {};
}

interface LineItem {
  descripcion: string;
  cantidad: number;
  unidad: string;
  precio_unitario: number;
  subtotal: number;
  codigo: string | null;
  // review state
  matchedProductId: string | null;
  matchedProductName: string | null;
  action: "match" | "create" | "skip";
}

function extractLineItems(doc: CaptureDoc): LineItem[] {
  const llm = parseLlmStructured(doc.ocr_json);
  const llmItems = llm?.items ?? [];
  if (llmItems.length > 0) {
    return llmItems.map((it) => ({
      descripcion: it.descripcion,
      cantidad: it.cantidad ?? 1,
      unidad: it.unidad ?? "unidad",
      precio_unitario: it.precio_unitario ?? 0,
      subtotal: it.subtotal ?? (it.cantidad ?? 1) * (it.precio_unitario ?? 0),
      codigo: it.codigo ?? null,
      matchedProductId: null,
      matchedProductName: null,
      action: "match",
    }));
  }
  // Fallback: structured_json items
  const sd = doc.structured_json?.data;
  if (sd?.items && sd.items.length > 0) {
    return sd.items.map((it) => ({
      descripcion: it.description,
      cantidad: it.quantity ?? 1,
      unidad: it.unit ?? "unidad",
      precio_unitario: it.unit_price ?? 0,
      subtotal: it.subtotal ?? (it.quantity ?? 1) * (it.unit_price ?? 0),
      codigo: it.sku ?? null,
      matchedProductId: null,
      matchedProductName: null,
      action: "match",
    }));
  }
  return [];
}

// ── Step 1: Proveedor ─────────────────────────────────────────────────────────

export interface SupplierDraft {
  nombre: string;
  cuit?: string;
  email?: string;
  phone?: string;
  contactName?: string;
  direccion?: string;
}

function StepSupplier({
  hint,
  tenantId,
  onNext,
}: {
  hint: { nombre?: string; cuit?: string; email?: string; phone?: string; direccion?: string };
  tenantId: string;
  onNext: (supplierId: string | null, draft: SupplierDraft) => void;
}) {
  const [search, setSearch] = useState(hint.nombre ?? "");
  const [results, setResults] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Supplier | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<SupplierDraft>({
    nombre: hint.nombre ?? "",
    cuit: hint.cuit ?? "",
    email: hint.email ?? "",
    phone: hint.phone ?? "",
    direccion: hint.direccion ?? "",
  });

  async function doSearch(q: string) {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/suppliers`, {
        credentials: "include",
        headers: clientRequestHeaders(tenantId),
      });
      if (res.ok) {
        const data = (await res.json()) as { suppliers?: Supplier[] };
        const all = data.suppliers ?? [];
        const norm = q.toLowerCase();
        setResults(all.filter((s) =>
          (s.nombre ?? s.name ?? "").toLowerCase().includes(norm)
        ));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (hint.nombre) void doSearch(hint.nombre);
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-muted-foreground mb-3">
          Renata detectó este proveedor en el documento. Confirmá si ya existe o creá uno nuevo.
        </p>
        {hint.nombre && (
          <div className="rounded-lg border bg-muted/40 p-3 text-sm space-y-1 mb-4">
            <p className="font-medium">{hint.nombre}</p>
            {hint.cuit && <p className="text-muted-foreground">CUIT: {hint.cuit}</p>}
            {hint.email && <p className="text-muted-foreground">{hint.email}</p>}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <input
            className="w-full pl-8 pr-3 py-2 text-sm border rounded-md bg-background"
            placeholder="Buscar proveedor existente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch(search)}
          />
        </div>
        <Button size="sm" variant="outline" onClick={() => doSearch(search)} disabled={loading}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Buscar"}
        </Button>
      </div>

      {results.length > 0 && (
        <div className="space-y-1.5">
          {results.map((s) => (
            <button
              key={s.id}
              onClick={() => setSelected(s)}
              className={cn(
                "w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors",
                selected?.id === s.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-muted/60",
              )}
            >
              <p className="font-medium">{s.name ?? s.nombre}</p>
              {s.cuit && <p className="text-xs text-muted-foreground">CUIT: {s.cuit}</p>}
            </button>
          ))}
        </div>
      )}

      {results.length === 0 && search && !loading && (
        <p className="text-sm text-muted-foreground text-center py-3">
          No se encontraron proveedores con ese nombre.
        </p>
      )}

      {creating && (
        <div className="rounded-lg border p-4 space-y-3 bg-muted/30">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Nuevo proveedor — completá los datos</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">Nombre *</span>
              <input
                className="w-full px-2.5 py-1.5 text-sm border rounded-md bg-background"
                value={draft.nombre}
                onChange={(e) => setDraft((d) => ({ ...d, nombre: e.target.value }))}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">CUIT</span>
              <input
                className="w-full px-2.5 py-1.5 text-sm border rounded-md bg-background"
                placeholder="30-00000000-0"
                value={draft.cuit ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, cuit: e.target.value }))}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">Email</span>
              <input
                className="w-full px-2.5 py-1.5 text-sm border rounded-md bg-background"
                type="email"
                value={draft.email ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">Teléfono</span>
              <input
                className="w-full px-2.5 py-1.5 text-sm border rounded-md bg-background"
                value={draft.phone ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">Contacto</span>
              <input
                className="w-full px-2.5 py-1.5 text-sm border rounded-md bg-background"
                placeholder="Nombre del contacto"
                value={draft.contactName ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, contactName: e.target.value }))}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">Dirección</span>
              <input
                className="w-full px-2.5 py-1.5 text-sm border rounded-md bg-background"
                value={draft.direccion ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, direccion: e.target.value }))}
              />
            </label>
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        {!creating && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setCreating(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Crear proveedor nuevo
          </Button>
        )}
        {creating && (
          <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>
            Cancelar
          </Button>
        )}
        <div className="flex-1" />
        <Button
          size="sm"
          disabled={!selected && !creating}
          onClick={() => {
            if (creating) {
              if (!draft.nombre.trim()) return;
              onNext(null, draft);
            } else if (selected) {
              onNext(selected.id, { nombre: selected.name ?? selected.nombre ?? "", cuit: selected.cuit ?? undefined });
            }
          }}
        >
          {selected ? `Usar ${selected.name ?? selected.nombre}` : creating ? "Crear y continuar" : "Continuar"}
          <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ── Step 2: Productos ─────────────────────────────────────────────────────────

function normalize(s: string) {
  return s.toLowerCase().replace(/\s+/g, " ").trim();
}

function ProductMatchRow({
  item,
  tenantId,
  onChange,
}: {
  item: LineItem;
  tenantId: string;
  onChange: (update: Partial<LineItem>) => void;
}) {
  const [search, setSearch] = useState(item.descripcion);
  const [results, setResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  async function doSearch(q: string, autoMatch = false) {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(
        `/api/products?q=${encodeURIComponent(q)}&limit=6`,
        { credentials: "include", headers: clientRequestHeaders(tenantId) },
      );
      if (res.ok) {
        const data = (await res.json()) as { items?: Product[]; products?: Product[] };
        const found = data.items ?? data.products ?? [];
        setResults(found);
        if (autoMatch && found.length > 0) {
          const normQ = normalize(q);
          const exact = found.find((p) => normalize(p.name ?? p.nombre ?? "") === normQ);
          if (exact) {
            onChange({ matchedProductId: exact.id, matchedProductName: exact.name ?? exact.nombre ?? "" });
            return;
          }
        }
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void doSearch(item.descripcion, true);
  }, []);

  useEffect(() => {
    if (item.matchedProductId) return;
    if (search.length < 3) return;
    const t = setTimeout(() => doSearch(search, false), 300);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div className={cn(
      "rounded-lg border p-3 space-y-2 transition-colors",
      item.action === "skip" && "opacity-50",
      item.matchedProductId && "border-primary/30 bg-primary/5",
      item.action === "create" && "border-amber-300/50 bg-amber-50/30 dark:bg-amber-950/10",
    )}>
      {/* Cabecera: descripción + acciones secundarias */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{item.descripcion}</p>
          <p className="text-xs text-muted-foreground">
            {item.cantidad} {item.unidad}
            {item.precio_unitario ? ` · $${item.precio_unitario.toLocaleString("es-AR")}` : ""}
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          {item.action !== "create" && (
            <button
              onClick={() => onChange({ action: "create", matchedProductId: null, matchedProductName: null })}
              className="px-2 py-1 text-xs rounded border border-border hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition-colors"
            >
              Crear nuevo
            </button>
          )}
          {item.action !== "skip" ? (
            <button
              onClick={() => onChange({ action: "skip", matchedProductId: null, matchedProductName: null })}
              className="px-2 py-1 text-xs rounded border border-border hover:bg-muted transition-colors text-muted-foreground"
            >
              Omitir
            </button>
          ) : (
            <button
              onClick={() => onChange({ action: "match", matchedProductId: null, matchedProductName: null })}
              className="px-2 py-1 text-xs rounded border border-border hover:bg-muted transition-colors"
            >
              Deshacer
            </button>
          )}
        </div>
      </div>

      {/* Estado: omitido */}
      {item.action === "skip" && (
        <p className="text-xs text-muted-foreground italic">Esta línea será ignorada.</p>
      )}

      {/* Estado: crear nuevo */}
      {item.action === "create" && (
        <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
          <span>Se creará &ldquo;{item.descripcion}&rdquo; como producto nuevo al confirmar.</span>
          <button
            onClick={() => onChange({ action: "match", matchedProductId: null, matchedProductName: null })}
            className="ml-auto underline text-muted-foreground hover:text-foreground shrink-0"
          >
            Cancelar
          </button>
        </div>
      )}

      {/* Estado: matchear (default) */}
      {item.action === "match" && (
        <div className="space-y-1.5">
          {item.matchedProductId ? (
            /* Matcheado — chip verde */
            <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-2 py-1.5">
              <Check className="h-3.5 w-3.5 text-primary shrink-0" />
              <span className="text-xs font-semibold text-primary">{item.matchedProductName}</span>
              <button
                onClick={() => {
                  onChange({ matchedProductId: null, matchedProductName: null });
                  setOpen(true);
                }}
                className="ml-auto text-xs text-muted-foreground underline hover:text-foreground shrink-0"
              >
                Cambiar
              </button>
            </div>
          ) : (
            /* Sin match — buscar */
            <>
              <div className="flex gap-1.5">
                <div className="relative flex-1">
                  {loading && <Loader2 className="absolute left-2 top-1.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                  <input
                    className={cn(
                      "w-full py-1 text-xs border rounded bg-background",
                      loading ? "pl-7 pr-2" : "pl-2 pr-2",
                    )}
                    placeholder="Buscar en catálogo..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch(search, false)}
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  onClick={() => doSearch(search, false)}
                  disabled={loading}
                >
                  <Search className="h-3 w-3" />
                </Button>
              </div>

              {/* Sugerencias */}
              {open && results.length > 0 && (
                <div className="space-y-0.5 rounded-md border bg-background p-1">
                  {results.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => {
                        onChange({ matchedProductId: p.id, matchedProductName: p.name ?? p.nombre ?? "" });
                        setOpen(false);
                      }}
                      className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-primary/10 hover:text-primary transition-colors flex items-center gap-1.5"
                    >
                      <Check className="h-3 w-3 shrink-0 text-primary/40" />
                      <span className="font-medium">{p.name ?? p.nombre}</span>
                      {(p.stockActual ?? p.stock) != null && (
                        <span className="ml-auto text-muted-foreground shrink-0">stock: {p.stockActual ?? p.stock}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {open && results.length === 0 && !loading && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>No encontrado en catálogo.</span>
                  <button
                    onClick={() => onChange({ action: "create", matchedProductId: null, matchedProductName: null })}
                    className="underline hover:text-foreground"
                  >
                    Crear nuevo
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StepProducts({
  doc,
  tenantId,
  onNext,
}: {
  doc: CaptureDoc;
  tenantId: string;
  onNext: (items: LineItem[]) => void;
}) {
  const [items, setItems] = useState<LineItem[]>(() => extractLineItems(doc));

  function updateItem(idx: number, update: Partial<LineItem>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...update } : it)));
  }

  const readyCount = items.filter((it) =>
    it.action === "skip" || it.matchedProductId || it.action === "create",
  ).length;

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Renata detectó {items.length} línea{items.length !== 1 ? "s" : ""} en el documento.
        Para cada una, elegí el producto existente o indicá si hay que crearlo.
      </p>

      {items.length === 0 && (
        <div className="text-center py-8 text-sm text-muted-foreground">
          No se detectaron líneas de productos en este documento.
        </div>
      )}

      <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
        {items.map((item, idx) => (
          <ProductMatchRow
            key={idx}
            item={item}
            tenantId={tenantId}
            onChange={(u) => updateItem(idx, u)}
          />
        ))}
      </div>

      <div className="flex items-center justify-between pt-2">
        <p className="text-xs text-muted-foreground">
          {readyCount} de {items.length} líneas resueltas
        </p>
        <Button size="sm" onClick={() => onNext(items)}>
          Revisar stock
          <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ── Step 3: Stock ─────────────────────────────────────────────────────────────

interface StockProposal {
  productId: string;
  productName: string;
  currentStock: number | null;
  delta: number;
  newStock: number;
  confirmed: boolean;
}

function StepStock({
  items,
  tenantId,
  onNext,
}: {
  items: LineItem[];
  tenantId: string;
  onNext: (proposals: StockProposal[]) => void;
}) {
  const matchedItems = items.filter(
    (it) => it.action === "match" && it.matchedProductId,
  );

  const [proposals, setProposals] = useState<StockProposal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const result: StockProposal[] = [];
      for (const item of matchedItems) {
        let currentStock: number | null = null;
        try {
          const res = await fetch(`/api/products?id=${item.matchedProductId}`, {
            credentials: "include",
            headers: clientRequestHeaders(tenantId),
          });
          if (res.ok) {
            const data = (await res.json()) as { product?: Product };
            currentStock = data.product?.stockActual ?? data.product?.stock ?? null;
          }
        } catch {
          // ignore
        }
        result.push({
          productId: item.matchedProductId!,
          productName: item.matchedProductName!,
          currentStock,
          delta: item.cantidad,
          newStock: (currentStock ?? 0) + item.cantidad,
          confirmed: true,
        });
      }
      setProposals(result);
      setLoading(false);
    }
    void load();
  }, []);

  function toggleConfirm(idx: number) {
    setProposals((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, confirmed: !p.confirmed } : p)),
    );
  }

  function setDelta(idx: number, delta: number) {
    setProposals((prev) =>
      prev.map((p, i) =>
        i === idx
          ? { ...p, delta, newStock: (p.currentStock ?? 0) + delta }
          : p,
      ),
    );
  }

  const confirmedCount = proposals.filter((p) => p.confirmed).length;

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Revisá la propuesta de actualización de stock. Podés ajustar las cantidades o excluir productos.
      </p>

      {proposals.length === 0 && (
        <div className="text-center py-8 text-sm text-muted-foreground">
          No hay productos para actualizar stock (ninguno fue matcheado).
        </div>
      )}

      <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
        {proposals.map((p, idx) => (
          <div
            key={p.productId}
            className={cn(
              "rounded-lg border p-3 flex items-center gap-3 transition-opacity",
              !p.confirmed && "opacity-50",
            )}
          >
            <button
              onClick={() => toggleConfirm(idx)}
              className={cn(
                "h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors",
                p.confirmed
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border",
              )}
            >
              {p.confirmed && <Check className="h-3 w-3" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{p.productName}</p>
              <p className="text-xs text-muted-foreground">
                Stock actual: {p.currentStock ?? "desconocido"} → {p.newStock}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-xs text-muted-foreground">+</span>
              <input
                type="number"
                min={0}
                value={p.delta}
                onChange={(e) => setDelta(idx, Number(e.target.value))}
                className="w-16 px-2 py-1 text-xs border rounded bg-background text-center"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2">
        <p className="text-xs text-muted-foreground">
          {confirmedCount} de {proposals.length} actualizaciones seleccionadas
        </p>
        <Button size="sm" onClick={() => onNext(proposals.filter((p) => p.confirmed))}>
          Ver resumen
          <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ── Step 4: Confirmar ─────────────────────────────────────────────────────────

function StepConfirm({
  doc,
  supplierId,
  supplierDraft,
  lineItems,
  stockProposals,
  tenantId,
  captureId,
  onDone,
}: {
  doc: CaptureDoc;
  supplierId: string | null;
  supplierDraft: SupplierDraft;
  lineItems: LineItem[];
  stockProposals: StockProposal[];
  tenantId: string;
  captureId: string;
  onDone: () => void;
}) {
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(
    doc.status === "processed" ? { ok: true, message: "Esta captura ya fue procesada." } : null,
  );

  const toCreate = lineItems.filter((it) => it.action === "create");
  const toMatch = lineItems.filter((it) => it.action === "match" && it.matchedProductId);
  const toSkip = lineItems.filter((it) => it.action === "skip");

  async function apply() {
    setApplying(true);
    try {
      // Si es proveedor nuevo, crearlo primero
      let resolvedSupplierId = supplierId;
      if (!supplierId && supplierDraft.nombre.trim()) {
        const sRes = await fetch("/api/suppliers", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...clientRequestHeaders(tenantId) },
          body: JSON.stringify({
            name: supplierDraft.nombre.trim(),
            cuit: supplierDraft.cuit?.trim() || undefined,
            email: supplierDraft.email?.trim() || undefined,
            phone: supplierDraft.phone?.trim() || undefined,
            contactName: supplierDraft.contactName?.trim() || undefined,
            direccion: supplierDraft.direccion?.trim() || undefined,
          }),
        });
        if (sRes.ok) {
          const sd = (await sRes.json()) as { supplier?: { id: string } };
          resolvedSupplierId = sd.supplier?.id ?? null;
        }
      }

      const res = await fetch(`/api/renata/capture`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...clientRequestHeaders(tenantId),
        },
        body: JSON.stringify({
          action: "apply_review",
          captureId,
          supplierId: resolvedSupplierId,
          supplierName: supplierDraft.nombre,
          stockUpdates: stockProposals.map((p) => ({
            productId: p.productId,
            productName: p.productName,
            delta: p.delta,
            newStock: p.newStock,
          })),
          productsToCreate: toCreate.map((it) => ({
            nombre: it.descripcion,
            codigo: it.codigo,
            precio: it.precio_unitario,
          })),
        }),
      });

      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean; updatedProducts?: number; errors?: string[]; error?: string; message?: string;
      };
      if (res.ok) {
        const n = data.updatedProducts ?? 0;
        const errs = data.errors?.length ? ` (${data.errors.length} error${data.errors.length !== 1 ? "es" : ""})` : "";
        setResult({ ok: true, message: `Stock actualizado en ${n} producto${n !== 1 ? "s" : ""}${errs}.` });
      } else {
        setResult({ ok: false, message: data.error ?? data.message ?? "Error al aplicar cambios." });
      }
    } catch (e) {
      setResult({ ok: false, message: e instanceof Error ? e.message : "Error de red." });
    } finally {
      setApplying(false);
    }
  }

  if (result) {
    return (
      <div className="py-8 text-center space-y-3">
        <div className={cn(
          "h-12 w-12 rounded-full flex items-center justify-center mx-auto",
          result.ok ? "bg-emerald-100 dark:bg-emerald-950/40" : "bg-red-100 dark:bg-red-950/40",
        )}>
          {result.ok
            ? <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            : <AlertCircle className="h-6 w-6 text-red-600" />}
        </div>
        <p className="font-medium">{result.ok ? "¡Listo!" : "Ocurrió un error"}</p>
        <p className="text-sm text-muted-foreground">{result.message}</p>
        <Button size="sm" variant="outline" onClick={onDone}>
          Volver a capturas
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Revisá el resumen antes de aplicar los cambios. Esta acción actualiza el stock en tu catálogo.
      </p>

      {/* Proveedor */}
      <div className="rounded-lg border p-3 space-y-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Proveedor</p>
        <div className="text-sm space-y-0.5">
          {supplierId ? (
            <span className="font-medium">{supplierDraft.nombre}</span>
          ) : (
            <span className="text-amber-600 font-medium">Nuevo: {supplierDraft.nombre || "sin nombre"}</span>
          )}
          {supplierDraft.cuit && <p className="text-xs text-muted-foreground">CUIT: {supplierDraft.cuit}</p>}
          {supplierDraft.email && <p className="text-xs text-muted-foreground">{supplierDraft.email}</p>}
          {supplierDraft.phone && <p className="text-xs text-muted-foreground">Tel: {supplierDraft.phone}</p>}
          {supplierDraft.direccion && <p className="text-xs text-muted-foreground">{supplierDraft.direccion}</p>}
        </div>
      </div>

      {/* Stock */}
      {stockProposals.length > 0 && (
        <div className="rounded-lg border p-3 space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Actualización de stock ({stockProposals.length})
          </p>
          {stockProposals.map((p) => (
            <div key={p.productId} className="flex items-center justify-between text-sm">
              <span>{p.productName}</span>
              <span className="text-emerald-600 font-medium">+{p.delta}</span>
            </div>
          ))}
        </div>
      )}

      {/* Crear productos */}
      {toCreate.length > 0 && (
        <div className="rounded-lg border border-amber-300/40 bg-amber-50/50 dark:bg-amber-950/10 p-3 space-y-1">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide">
            Productos a crear ({toCreate.length})
          </p>
          {toCreate.map((it, i) => (
            <p key={i} className="text-sm">{it.descripcion}</p>
          ))}
        </div>
      )}

      {/* Omitidos */}
      {toSkip.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {toSkip.length} línea{toSkip.length !== 1 ? "s" : ""} omitida{toSkip.length !== 1 ? "s" : ""} (no se actualiza stock).
        </p>
      )}

      <div className="flex gap-2 pt-2">
        <div className="flex-1" />
        <Button
          size="sm"
          onClick={apply}
          disabled={applying}
          className="gap-1.5"
        >
          {applying && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Aplicar cambios
        </Button>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function CaptureReviewPageInner() {
  const { captureId } = Route.useParams();
  const tenantId = useWorkspaceTenantId();
  const navigate = useNavigate();

  const [doc, setDoc] = useState<CaptureDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<StepId>("supplier");

  // State accumulated across steps
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierDraft, setSupplierDraft] = useState<SupplierDraft>({ nombre: "" });
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [stockProposals, setStockProposals] = useState<StockProposal[]>([]);

  useEffect(() => {
    if (!tenantId || !captureId) return;
    void (async () => {
      const res = await fetch(`/api/renata/capture?view=document&id=${captureId}`, {
        credentials: "include",
        headers: clientRequestHeaders(tenantId),
      });
      if (res.ok) {
        const data = (await res.json()) as { document?: CaptureDoc };
        setDoc(data.document ?? null);
      }
      setLoading(false);
    })();
  }, [tenantId, captureId]);

  if (!tenantId) return null;

  const stepIdx = STEPS.findIndex((s) => s.id === step);

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/app/renata/inbox" })}
          className="gap-1.5 text-muted-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Capturas
        </Button>
        {doc && (
          <Badge variant="outline" className="text-xs">
            {DOC_CLASS_ES[doc.doc_class] ?? doc.doc_class} · {CONFIDENCE_ES[doc.overall_confidence] ?? doc.overall_confidence}
          </Badge>
        )}
      </div>

      {loading && (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && !doc && (
        <div className="text-center py-16 space-y-2">
          <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto" />
          <p className="text-sm text-muted-foreground">Captura no encontrada.</p>
        </div>
      )}

      {!loading && doc && (
        <>
          {/* Nombre del archivo */}
          <div>
            <h1 className="text-base font-semibold">{doc.filename}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Motor OCR: {OCR_ENGINE_ES[doc.ocr_json.engine ?? ""] ?? doc.ocr_json.engine ?? "—"} · {labelStatus(INBOX_STATUS_ES, doc.status)}
            </p>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-0">
            {STEPS.map((s, i) => {
              const done = i < stepIdx;
              const active = i === stepIdx;
              const Icon = s.icon;
              return (
                <div key={s.id} className="flex items-center">
                  <div
                    className={cn(
                      "flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors",
                      done && "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30",
                      active && "text-primary bg-primary/10",
                      !done && !active && "text-muted-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {s.label}
                  </div>
                  {i < STEPS.length - 1 && (
                    <ChevronRight className="h-3.5 w-3.5 text-border mx-0.5 shrink-0" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Step content */}
          <div className="rounded-xl border bg-card p-5">
            {step === "supplier" && (
              <StepSupplier
                hint={extractSupplierHint(doc)}
                tenantId={tenantId}
                onNext={(id, draft) => {
                  setSupplierId(id);
                  setSupplierDraft(draft);
                  setStep("products");
                }}
              />
            )}
            {step === "products" && (
              <StepProducts
                doc={doc}
                tenantId={tenantId}
                onNext={(items) => {
                  setLineItems(items);
                  setStep("stock");
                }}
              />
            )}
            {step === "stock" && (
              <StepStock
                items={lineItems}
                tenantId={tenantId}
                onNext={(proposals) => {
                  setStockProposals(proposals);
                  setStep("confirm");
                }}
              />
            )}
            {step === "confirm" && (
              <StepConfirm
                doc={doc}
                supplierId={supplierId}
                supplierDraft={supplierDraft}
                lineItems={lineItems}
                stockProposals={stockProposals}
                tenantId={tenantId}
                captureId={captureId}
                onDone={() => navigate({ to: "/app/renata/inbox" })}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function CaptureReviewPage() {
  return (
    <RenataFeatureGate featureId="guided_review">
      <CaptureReviewPageInner />
    </RenataFeatureGate>
  );
}
